type TelemetryFields = Record<string, unknown>;
type TelemetryEvent = {
  contexts?: { trace?: { data?: TelemetryFields } };
  request?: {
    url?: string;
    query_string?: unknown;
    data?: unknown;
    headers?: unknown;
    cookies?: unknown;
  };
  user?: { id?: string | number };
};

// Keep the resource path and source frames useful while excluding user queries,
// location selection, credentials, and fragments from automatically captured HTTP data.
function redactUrl(value: string): string {
  const redactPath = (path: string) =>
    path.replace(/(\/(?:api\/auth\/)?reset-password\/)[^/]+/g, "$1[redacted]");
  try {
    const url = new URL(value);
    url.search = "";
    url.hash = "";
    url.username = "";
    url.password = "";
    url.pathname = redactPath(url.pathname);
    return url.toString();
  } catch {
    return redactPath(value.split(/[?#]/, 1)[0] ?? "");
  }
}

function sanitizeFields(data: TelemetryFields): TelemetryFields {
  const safe = { ...data };
  for (const key of [
    "url",
    "http.url",
    "http.target",
    "url.full",
    "url.path",
    "from",
    "to",
  ]) {
    if (typeof safe[key] === "string") safe[key] = redactUrl(safe[key]);
  }
  for (const key of [
    "http.query",
    "url.query",
    "url.fragment",
    "query_string",
    "http.client_ip",
    "net.peer.ip",
    "net.host.ip",
    "client.address",
  ])
    delete safe[key];
  return safe;
}

export function sanitizeTelemetryBreadcrumb<
  T extends { data?: TelemetryFields },
>(value: T): T {
  if (value.data) value.data = sanitizeFields(value.data);
  return value;
}

export function sanitizeTelemetrySpan<
  T extends { description?: string; data?: TelemetryFields },
>(value: T): T {
  if (value.data) value.data = sanitizeFields(value.data);
  if (
    value.description &&
    /^(?:[A-Z]+ )?(?:https?:\/\/|\/)/.test(value.description)
  ) {
    value.description = value.description.replace(
      /(?:https?:\/\/|\/)[^\s]+/g,
      redactUrl,
    );
  }
  return value;
}

export function sanitizeTelemetryEvent<T extends TelemetryEvent>(value: T): T {
  if (value.contexts?.trace?.data)
    value.contexts.trace.data = sanitizeFields(value.contexts.trace.data);
  if (value.request) {
    if (value.request.url) value.request.url = redactUrl(value.request.url);
    delete value.request.query_string;
    delete value.request.data;
    delete value.request.headers;
    delete value.request.cookies;
  }
  if (value.user)
    value.user =
      value.user.id === undefined ? undefined : { id: value.user.id };
  return value;
}
